import React, { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import InputTextAreaComponent from "presentation/components/Shared/InputTextAreaComponent";
import { Image } from "primereact/image";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { Parameter } from "domain/entities/parameter";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import {
  DocumentFilesPayload,
  UpdateInput,
} from "domain/entities/document-files";
import {
  useCreateDocumentFilesMutation,
  useUpdateDocumentFilesMutation,
} from "presentation/hooks/use-query-document-files";
import { DocumentFilesType } from "domain/entities/document-files";
import { httpClient } from "infra/axios/axios-api";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { MultiSelect } from "primereact/multiselect";
import MultiSelectComponent from "presentation/components/Shared/MultiSelectComponent";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { RoleInputFilter } from "domain/models/role-input-filter";
import { Role } from "domain/entities/role";
import { User } from "domain/entities/user";

type Props = {
  mode: "create" | "edit" | "view";
  initialData?: any;
  onSuccess?: () => void;
  type: DocumentFilesType;
};

type FormType = {
  id?: string;
  title: string;
  category: string;
  description: string;
  type: DocumentFilesType;
  order: number;
  isPrivate: boolean;
  allowedRoles: string[];
  file: File | null;
  urlPath: string;
};

const emptyForm: FormType = {
  title: "",
  category: "",
  description: "",
  type: DocumentFilesType.TUTORIAL,
  order: 0,
  isPrivate: false,
  allowedRoles: [],
  file: null,
  urlPath: "",
};

const DocumentFilesForm: React.FC<Props> = ({
  onSuccess,
  type,
  initialData,
  mode,
}) => {
  const toast = useRef<Toast>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [roles, setRoles] = useState<Parameter[]>();
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const isViewMode = mode === "view";
  const [form, setForm] = useState<FormType>({
    ...emptyForm,
    type,
  });

  const { data: categoriesResponse } = useParameterByFilter({
    page: 1,
    limit: 500,
    type: "categoryDocsOptions",
  } as BaseInputFilter);

  const categoryOptionsData =
    categoriesResponse?.data?.map((item: any) => ({
      label: item.value,
      value: item.value,
    })) || [];

  const { data: rolesData, refetch: refetchRoles } = useRoleByFilter({
    limit: 100,
  } as RoleInputFilter);

  const parameters = categoriesResponse?.data ?? [];

  const roleOptions = parameters
    .filter((item) => item.value !== "Geral")
    .map((item) => item.value);

  const categoryOptions: Parameter[] = parameters.map((item) => ({
    id: item.id,
    type: item.type,
    value: item.value,
  }));

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && initialData) {
      setForm({
        id: initialData.id,
        title: initialData.title,
        category: initialData.category,
        description: initialData.description,
        type: initialData.type,
        order: initialData.order,
        isPrivate: initialData.isPrivate,
        allowedRoles: initialData.allowedRoles,
        file: null,
        urlPath: initialData.urlPath,
      });

      setExistingFile(initialData.urlPath);

      if (
        initialData.urlPath &&
        /\.(jpg|jpeg|png|gif|webp)$/i.test(initialData.urlPath)
      ) {
        loadPreview(initialData.urlPath);
      } else {
        setPreviewUrl(null);
      }
    }
  }, [initialData, mode]);

  useEffect(() => {
    const formatedRoles = rolesData?.data?.map((item) => {
      const roles: Parameter = {
        id: item.id,
        value: item.name,
        type: "role",
      };
      return roles;
    });
    setRoles(formatedRoles ?? []);
  }, [rolesData]);

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && initialData?.allowedRoles && rolesData?.data) {
      const allowedNames: string[] = initialData.allowedRoles;
      const matchedRoles = rolesData.data.filter((role) =>
        allowedNames.includes(role.name),
      );
      setSelectedRoles(matchedRoles);
    } else if (mode === "create") {
      setSelectedRoles([]);
    }
  }, [initialData, mode, rolesData]);

  const createMutation = useCreateDocumentFilesMutation();
  const updateMutation = useUpdateDocumentFilesMutation();

  const loadPreview = async (path: string) => {
    try {
      const response = await httpClient.get(`/assets/${path}`);

      setPreviewUrl(response.data.url);
    } catch (err) {
      console.error(err);
      setPreviewUrl(null);
    }
  };

  const handleUpload = async (event: FileUploadHandlerEvent) => {
    const file = event.files[0];

    if (!file) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Nenhum ficheiro selecionado.",
        life: 3000,
      });
      return;
    }

    const blockedExtensions = [
      "exe",
      "cmd",
      "bat",
      "sh",
      "msi",
      "js",
      "php",
      "py",
      "jar",
      "vbs",
    ];
    const previewExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension && blockedExtensions.includes(extension)) {
      toast.current?.show({
        severity: "error",
        summary: "Ficheiro bloqueado",
        detail: `A extensão .${extension} não é permitida.`,
        life: 4000,
      });
      return;
    }

    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    if (previewExtensions.includes(extension ?? "")) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    const fileName = form.title.trim().replace(/\s+/g, "_");

    const folder =
      type === DocumentFilesType.REGULATION ? "regulations" : "tutorials";

    const urlPath = `${folder}/${fileName}`;

    setForm((prev) => ({
      ...prev,
      file,
      urlPath,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!form.title.trim()) {
        throw new Error("O título é obrigatório.");
      }

      if (!form.category.trim()) {
        throw new Error("A categoria é obrigatória.");
      }

      if (mode === "create" && !form.file) {
        throw new Error("O ficheiro é obrigatório.");
      }

      if (selectedRoles.length > 0)
        form.allowedRoles = selectedRoles.map((r) => r.name);
      if (form.isPrivate && form.allowedRoles.length === 0) {
        throw new Error("Selecione pelo menos um cargo com acesso.");
      }

      setIsUploading(true);

      let fileUrl = form.urlPath;

      if (form.file) {
        const formDataUpload = new FormData();

        formDataUpload.append("file", form.file);
        formDataUpload.append("fileName", form.urlPath);

        const uploadResponse = await httpClient.post(
          `/assets/${form.urlPath}`,
          formDataUpload,
        );

        fileUrl =
          uploadResponse.data.filePath ??
          uploadResponse.data.url ??
          form.urlPath;
        console.log("Ficheiro enviado com sucesso:", fileUrl);
      }

      const basePayload: UpdateInput = {
        title: form.title,
        description: form.description,
        category: form.category,
        urlPath: fileUrl,
        order: form.order,
        isPrivate: form.isPrivate,
        allowedRoles: form.isPrivate ? selectedRoles.map((r) => r.name) : [],
      };

      if (mode === "create") {
        const createPayload: DocumentFilesPayload = {
          type: form.type,
          ...basePayload,
        };

        await createMutation.mutateAsync(createPayload);
      }

      if (mode === "edit" && form.id) {
        const updatePayload: UpdateInput = {
          id: form.id,
          ...basePayload,
        };

        await updateMutation.mutateAsync(updatePayload);

        if (form.file && existingFile && existingFile !== fileUrl) {
          await httpClient.delete(`/assets/${existingFile}`);
        }
      }

      setForm({
        ...emptyForm,
        type,
      });

      onSuccess?.();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error?.message ?? "Erro ao guardar documento.",
        life: 5000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} position="bottom-right" />

      <div className="p-fluid">
        <div className="grid">
          {/* Modo de Visualização: Tipo e Ordem */}
          {mode === "view" && (
            <>
              <div className="col-12 md:col-6 field mt-2">
                <InputTextComponent
                  fieldName="type"
                  label="Tipo"
                  currentObject={form}
                  getValue={
                    form.type === DocumentFilesType.REGULATION
                      ? "Regulamento"
                      : "Tutorial"
                  }
                  setValue={() => {}}
                  isDisable={isViewMode}
                />
              </div>

              <div className="col-12 md:col-6 field mt-2">
                <InputNumberComponent
                  fieldName="order"
                  label="Ordem"
                  min={0}
                  currentObject={form}
                  getValue={form.order}
                  setValue={setForm}
                  isDisable={isViewMode}
                />
              </div>
            </>
          )}

          {/* Título e Categoria */}
          <div className="col-12 md:col-6 field mt-2">
            <InputTextComponent
              fieldName="title"
              label="Título *"
              currentObject={form}
              getValue={form.title}
              setValue={setForm}
              isDisable={isViewMode}
            />
          </div>

          <div className="col-12 md:col-6 field mt-2">
            <DropdownComponent
              fieldName="category"
              label="Categoria *"
              currentObject={form}
              getValue={form.category}
              setValue={setForm}
              options={categoryOptions}
              isDisable={isViewMode}
            />
          </div>

          {/* Descrição */}
          <div className="col-12 field mt-2">
            <InputTextAreaComponent
              fieldName="description"
              label="Descrição"
              currentObject={form}
              getValue={form.description}
              setValue={setForm}
              isDisable={isViewMode}
              maxLength={500}
            />
          </div>

          {/* Opção Privado */}
          <div className="col-12 field mt-2">
            <div className="flex align-items-center gap-2 mb-2">
              <Checkbox
                inputId="private"
                checked={form.isPrivate}
                disabled={isViewMode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isPrivate: e.checked ?? false,
                    allowedRoles: e.checked ? form.allowedRoles : [],
                  })
                }
              />
              <label htmlFor="private" className="font-semibold cursor-pointer select-none">
                Este ficheiro é privado
              </label>
            </div>
          </div>

          {/* MultiSelect para Cargos */}
          {form.isPrivate && (
            <div className="col-12 field mt-2">
              <div className="w-full">
                <MultiSelectComponent
                  options={roles ?? []}
                  label="Cargos com Acesso"
                  isValueId={true}
                  fieldName="allowedRoles"
                  currentObject={selectedRoles}
                  getValue={selectedRoles.map((r) => r.id)}
                  setValue={(ids: string[]) => {
                    const selected =
                      roles?.filter(
                        (role) =>
                          role.id !== undefined && ids.includes(role.id),
                      ) ?? [];
                    const selectedRolesList = selected.map(
                      (role: Parameter) => {
                        const roleData = rolesData?.data?.find(
                          (r) => r.id === role.id,
                        );
                        return roleData as Role;
                      },
                    );
                    setSelectedRoles(selectedRolesList);
                  }}
                />
              </div>
            </div>
          )}

          {/* Secção de Ficheiro */}
          <div className="col-12 field mt-3">
            <label className="block mb-2 font-bold text-700">Ficheiro *</label>
            
            {isViewMode ? (
              // Visualização amigável em Modo View
              <div className="flex align-items-center justify-content-between p-3 border-1 surface-border border-round surface-50">
                <div className="flex align-items-center gap-3 overflow-hidden">
                  <i className="pi pi-file text-primary text-2xl"></i>
                  <div className="overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                    <span className="font-semibold text-800">
                      {existingFile ? existingFile.split("/").pop() : "Sem ficheiro"}
                    </span>
                  </div>
                </div>
                {existingFile && (
                  <Button
                    type="button"
                    icon="pi pi-download"
                    className="p-button-rounded p-button-text"
                    tooltip="Descarregar Ficheiro"
                    tooltipOptions={{ position: "top" }}
                    onClick={async () => {
                      try {
                        const isImage =
                          /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(
                            form.urlPath,
                          );

                        const endpoint = isImage
                          ? `/assets/inline/${form.urlPath}`
                          : `/assets/${form.urlPath}`;

                        const response = await httpClient.get(endpoint);

                        window.open(response.data.url, "_blank");
                      } catch (error) {
                        console.error("Erro ao obter URL do ficheiro:", error);
                      }
                    }}
                  />
                )}
              </div>
            ) : (
              // Modo de Edição / Criação
              <div>
                <div className="flex align-items-center gap-2 mb-2">
                  <FileUpload
                    name="document"
                    ref={fileUploadRef}
                    key={uploadKey}
                    customUpload
                    uploadHandler={handleUpload}
                    mode="basic"
                    chooseLabel="Selecionar Ficheiro"
                    auto
                    style={{ width: "auto" }}
                  />

                  {form.file && (
                    <Button
                      type="button"
                      severity="danger"
                      outlined
                      icon="pi pi-trash"
                      label="Remover"
                      style={{ width: "auto" }}
                      onClick={() => {
                        if (previewUrl?.startsWith("blob:")) {
                          URL.revokeObjectURL(previewUrl);
                        }

                        fileUploadRef.current?.clear();
                        setUploadKey((k) => k + 1);
                        setPreviewUrl(null);

                        setForm((prev) => ({
                          ...prev,
                          file: null,
                          urlPath: existingFile ?? "",
                        }));
                      }}
                    />
                  )}
                </div>

                {(form.file || existingFile) && (
                  <small className="block text-600 mb-2">
                    Ficheiro selecionado:{" "}
                    <span className="font-semibold text-800">
                      {form.file ? form.file.name : existingFile?.split("/").pop()}
                    </span>
                  </small>
                )}
              </div>
            )}

            {/* Pré-visualização de imagem se disponível */}
            {previewUrl && (
              <div className="mt-3 border-1 surface-border border-round p-2 inline-block surface-card shadow-1">
                <Image src={previewUrl} alt="Preview" preview width="180" className="max-w-full block border-round" />
              </div>
            )}
          </div>
        </div>

        {/* Botões de Ação */}
        {mode !== "view" && (
          <div className="flex justify-content-end mt-4">
            <Button
              label="Gravar"
              icon="pi pi-save"
              onClick={handleSubmit}
              style={{ width: "auto" }}
              loading={
                createMutation.isPending ||
                updateMutation.isPending ||
                isUploading
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentFilesForm;
